import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Row, Col } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, clearError } from '../../redux/slices/authSlice';
import { sendVerificationCode } from '../../services/authService';
import logger from '../../utils/logger';
import './RegisterModal.css';
import trackingManager from '../../services/trackingManager';
import languageManager from '../../utils/languageManager';
import { isExtensionEnvironment } from '../../utils/extensionUtils';

const RegisterModal = ({ visible, onClose, onShowLogin }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const [countdown, setCountdown] = useState(0);
  const [verificationCodeError, setVerificationCodeError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  
  // 当弹窗打开时清除之前的错误信息
  useEffect(() => {
    if (visible) {
      dispatch(clearError());
      setVerificationCodeError('');
    }
  }, [visible, dispatch]);
  
  // 处理倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);
  
  // 处理发送验证码
  const handleSendVerificationCode = async () => {
    try {
      // 验证邮箱
      await form.validateFields(['email']);
      const email = form.getFieldValue('email');
      
      // 设置发送中状态
      setSendingCode(true);
      
      try {
        // 调用发送验证码 API
        await sendVerificationCode(email);
        
        // 设置倒计时
        setCountdown(60);
        messageApi.open({
          type: 'success',
          content: languageManager.t('auth.codeSentTo') + ' ' + email,
        });
      } catch (error) {
        // 处理 API 错误
        logger.error('发送验证码失败:', error);
        messageApi.open({
          type: 'error',
          content: error.message || '发送验证码失败，请稍后重试',
        });
      } finally {
        setSendingCode(false);
      }
    } catch (error) {
      // 邮箱验证失败
      logger.error('邮箱验证失败:', error);
    }
  };
  
  // 处理注册
  const handleSubmit = async (values) => {
    try {
      // 清除之前的错误信息
      dispatch(clearError());
      setVerificationCodeError('');
      
      // 上报用户注册埋点
      trackingManager.reportUserAuth('REGISTER', values.email);
      
      // 调用 Redux action 进行注册
      const userData = {
        email: values.email,
        password: values.password,
        code: values.verificationCode, // 添加验证码
        username: values.email.split('@')[0] // 可选：使用邮箱前缀作为默认用户名
      };
      
      const resultAction = await dispatch(registerUser(userData));
      
      // 检查注册结果
      if (registerUser.fulfilled.match(resultAction)) {
        // 注册成功，显示成功消息
        messageApi.open({
          type: 'success',
          content: languageManager.t('auth.registerSuccess'),
        });
        // 重置表单并切换到登录页面
        form.resetFields();
        onShowLogin();
      }
      // 如果有错误，将在 Redux state 的 error 字段中体现，会在界面上显示
    } catch (error) {
      logger.error('[RegisterModal] 注册出错:', error);
    }
  };

  // 返回登录页面
  const handleGoToLogin = () => {
    form.resetFields();
    dispatch(clearError());
    onShowLogin();
  };

  // 打开HTML页面的通用函数
  const openHtmlPage = (path) => {
    // 移除路径开头的斜杠（如果有）
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
    // 在扩展环境中，使用 chrome.runtime.getURL() 获取正确的路径
    if (isExtensionEnvironment() && typeof window.chrome !== 'undefined' && window.chrome.runtime && window.chrome.runtime.getURL) {
      const url = window.chrome.runtime.getURL(cleanPath);
      return window.open(url, '_blank');
    }
    
    // 在网页环境中，使用相对路径
    return window.open(path, '_blank');
  };

  return (
    <Modal
      title={languageManager.t('auth.register')}
      open={visible}
      onCancel={() => {
        form.resetFields();
        dispatch(clearError());
        onClose();
      }}
      footer={null}
      maskClosable={false}
      centered
      className="register-modal"
    >
      {contextHolder}
      <Form
        form={form}
        name="registerForm"
        onFinish={handleSubmit}
        scrollToFirstError
        layout="vertical"
      >
        {error && (
          <div className="register-error-message">
            {error}
          </div>
        )}
        
        <Form.Item
          name="email"
          rules={[
            { required: true, message: languageManager.t('auth.enterEmail') },
            { type: 'email', message: languageManager.t('auth.enterValidEmail') }
          ]}
        >
          <Input 
            prefix={<MailOutlined />} 
            placeholder={languageManager.t('auth.email')} 
            size="large"
            autoComplete="email"
          />
        </Form.Item>
        
        <Form.Item
          name="verificationCode"
          rules={[
            { required: true, message: languageManager.t('auth.enterVerificationCode') },
            { len: 6, message: languageManager.t('auth.codeShouldBeSixDigits') }
          ]}
          validateStatus={verificationCodeError ? "error" : ""}
          help={verificationCodeError}
        >
          <Row gutter={8}>
            <Col span={16}>
              <Input
                prefix={<SafetyOutlined />}
                placeholder={languageManager.t('auth.verificationCode')}
                size="large"
              />
            </Col>
            <Col span={8}>
              <Button
                size="large"
                className="verify-code-button"
                onClick={handleSendVerificationCode}
                disabled={countdown > 0 || sendingCode}
                loading={sendingCode}
                block
              >
                {countdown > 0 ? `${countdown}${languageManager.t('auth.resendAfter')}` : (sendingCode ? languageManager.t('auth.sending') : languageManager.t('auth.getVerificationCode'))}
              </Button>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            { required: true, message: languageManager.t('auth.enterPassword') },
            { min: 6, max: 32, message: languageManager.t('auth.passwordLength') }
          ]}
          hasFeedback
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={languageManager.t('auth.setPassword')}
            size="large"
            autoComplete="new-password"
            allowClear={false}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          hasFeedback
          rules={[
            { required: true, message: languageManager.t('auth.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(languageManager.t('auth.passwordsDoNotMatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={languageManager.t('auth.confirmPassword')}
            size="large"
            autoComplete="new-password"
            allowClear={false}
          />
        </Form.Item>

        <Form.Item
          name="agreement"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error(languageManager.t('auth.alreadyRead') + ' ' + languageManager.t('auth.userAgreement') + ' ' + languageManager.t('auth.and') + ' ' + languageManager.t('auth.privacyPolicyText'))),
            },
          ]}
        >
          <div className="agreement-checkbox">
            <input type="checkbox" id="agreement" />
            <label htmlFor="agreement">
              {languageManager.t('auth.alreadyRead')}{' '}
              <Button 
                type="link" 
                className="agreement-link" 
                onClick={(e) => {
                  e.preventDefault();
                  openHtmlPage('/user-agreement.html');
                }}
              >
                {languageManager.t('auth.userAgreement')}
              </Button>
              {' '}{languageManager.t('auth.and')}{' '}
              <Button 
                type="link" 
                className="agreement-link" 
                onClick={(e) => {
                  e.preventDefault();
                  openHtmlPage('/privacy-policy.html');
                }}
              >
                {languageManager.t('auth.privacyPolicyText')}
              </Button>
            </label>
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
          >
            {languageManager.t('auth.register')}
          </Button>
        </Form.Item>
        
        <div className="register-form-footer">
          <span>{languageManager.t('auth.haveAccount')}</span>
          <Button type="link" onClick={handleGoToLogin}>
            {languageManager.t('auth.loginNow')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default RegisterModal; 